import {
  Card,
  Marker,
  Grid,
  Spinner,
  Timeline,
} from '@design-systems/canvas-ui'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutline'
import { ErrorDialog } from 'mvscommoncomponents'
import { useCallback, useEffect, useState } from 'react'
import { getCapabilityAuditTrails } from '../../services/lcmService'

const CapabilityAuditTrail = (props) => {
  const [capAuditData, setCapAuditData] = useState([])
  const [isFetching, setIsFetching] = useState(true)
  const [isFailed, setIsFailed] = useState(false)

  const getCapabilityAudits = useCallback(async () => {
    try {
      setIsFetching(true)
      setIsFailed(false)

      const response = await getCapabilityAuditTrails(
        props.vettingType,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )

      setCapAuditData(Array.isArray(response) ? response : [])
      setIsFetching(false)
    } catch (e) {
      setIsFetching(false)
      setIsFailed(true)
      setCapAuditData([])
    }
  }, [
    props.vettingType,
    props.supplierId,
    props.locationId,
    props.manufacturingTypeId,
  ])

  useEffect(() => {
    if (props.isReadonly && props.supplierId) {
      getCapabilityAudits()
    }
  }, [props.isReadonly, props.supplierId, getCapabilityAudits])

  const getBy = (event) => event?.capabilities_updated_by || {}
  const getOn = (event) => event?.capabilities_updated_on || ''
  const getApproved = (event) => event?.approved_capabilities || []
  const getDisapproved = (event) => event?.disapproved_capabilities || []
  const getUnchanged = (event) => event?.unchanged_capabilities || []

  const capLabel = (cap) =>
    [cap?.primary_type, cap?.secondary_type, cap?.tertiary_type]
      .filter(Boolean)
      .join(' - ')

  const countCaps = (records = []) =>
    records.reduce((sum, r) => sum + (r?.capabilities?.length || 0), 0)

  const eventChangeCount = (event) =>
    countCaps(getApproved(event)) + countCaps(getDisapproved(event))

  const totalEvents = capAuditData.length
  const totalChanges = capAuditData.reduce(
    (sum, e) => sum + eventChangeCount(e),
    0,
  )

  const buildApprovalTypeBlocks = (event) => {
    const map = new Map()

    const add = (records, bucket) => {
      records.forEach((rec) => {
        const typeName = rec?.approval_type_name || ''
        if (!map.has(typeName)) {
          map.set(typeName, {
            approvalTypeName: typeName,
            approved: [],
            removed: [],
            unchanged: [],
          })
        }
        const block = map.get(typeName)
        const caps = (rec?.capabilities || []).map(capLabel)
        block[bucket].push(...caps)
      })
    }

    add(getApproved(event), 'approved')
    add(getDisapproved(event), 'removed')
    add(getUnchanged(event), 'unchanged')

    return Array.from(map.values())
  }

  const ApprovalTypeCard = ({ title, approved, removed, unchanged }) => {
    const hasAnything = approved.length || removed.length || unchanged.length
    if (!hasAnything) return null

    return (
      <Card
        variant="border"
        className="cap-audit-card hc8-mt-2x hc8-bg-subdued"
      >
        <div className="hc8-fs-150 cap-audit__strong">{title}</div>

        {approved.length > 0 && (
          <>
            <div className="cap-audit-card__section hc8-clr-success">
              Capabilities Approved
            </div>
            <ul className="cap-audit-card__list hc8-clr-success">
              {approved.map((t, i) => (
                <li key={`${title}-ap-${i}`}>{t}</li>
              ))}
            </ul>
          </>
        )}

        {removed.length > 0 && (
          <>
            <div className="cap-audit-card__section hc8-clr-error">
              Capabilities Approval Revoked
            </div>
            <ul className="cap-audit-card__list hc8-clr-error">
              {removed.map((t, i) => (
                <li key={`${title}-rm-${i}`}>{t}</li>
              ))}
            </ul>
          </>
        )}

        {unchanged.length > 0 && (
          <>
            <div className="cap-audit-card__section hc8-clr-placeholder">
              Unchanged Capabilities
            </div>
            <ul className="cap-audit-card__list hc8-clr-placeholder">
              {unchanged.map((t, i) => (
                <li key={`${title}-un-${i}`}>{t}</li>
              ))}
            </ul>
          </>
        )}
      </Card>
    )
  }

  return (
    <Grid.Container className="cap-audit hc8-wth-100">
      <Grid.Item
        size={{ xs: 12 }}
        className="cap-audit__header hc8-mt-3x hc8-pt-none hc8-pb-none"
      >
        <div className="hc8-fs-200 cap-audit__strong">Approval History</div>
        <div className="hc8-clr-disabled hc8-mt-1x">
          Complete audit trail of all capability approval changes
        </div>
      </Grid.Item>

      {/* Totals */}
      <Grid.Item
        size={{ xs: 12 }}
        className="cap-audit__summary total-events-summary hc8-mt-3x hc8-pv-3x hc8-bg-subdued"
      >
        <div className="cap-audit-totals">
          <div>
            Total Events:{' '}
            <span className="cap-audit-totals__value">{totalEvents}</span>
          </div>
          <span className="cap-audit-totals__sep">|</span>
          <div>
            Total Changes:{' '}
            <span className="cap-audit-totals__value">{totalChanges}</span>
          </div>
        </div>
      </Grid.Item>

      {/* Body */}
      <Grid.Container
        className="cap-audit__body hc8-mt-none hc8-pt-3x"
        id="capability-audit"
      >
        {isFetching && !isFailed && (
          <Grid.Item
            size={{ xs: 12 }}
            className="cap-audit__state hc8-bg-base hc8-mt-3x"
          >
            <Spinner size="lg" />
          </Grid.Item>
        )}

        {!isFetching && isFailed && (
          <Grid.Item
            size={{ xs: 12 }}
            className="cap-audit__state hc8-bg-base hc8-mt-3x"
          >
            <ErrorOutlineRoundedIcon
              className="hc8-clr-error"
              onClick={() => getCapabilityAudits()}
            />
          </Grid.Item>
        )}

        {!isFetching &&
          !isFailed &&
          (capAuditData.length === 0 ? (
            <Grid.Item size={{ xs: 12 }}>
              <ErrorDialog />
            </Grid.Item>
          ) : (
            <Timeline className="hc8-pt-none hc8-pl-3x hc8-pr-3x hc8-pb-2x hc8-mt-none">
              {capAuditData.map((event, idx) => {
                const changes = eventChangeCount(event)
                const userName = getBy(event)?.name || ''
                const dateText = getOn(event)
                const blocks = buildApprovalTypeBlocks(event)

                const markerColor =
                  countCaps(getDisapproved(event)) > 0 ? 'red' : 'green'
                const changeLabel = changes < 2 ? 'change' : 'changes'

                return (
                  <Timeline.Item
                    key={`cap-audit-${idx}`}
                    className="hc8-mt-none hc8-mb-2x"
                  >
                    <div>
                      <div className="cap-audit-event__title-row">
                        <div className="hc8-fs-350 cap-audit__strong">
                          Capability Update
                        </div>
                        <Marker
                          size="sm"
                          color={markerColor}
                          className="cap-audit-event__chip"
                        >
                          {changes} {changeLabel}
                        </Marker>
                      </div>

                      <div className="cap-audit-event__meta hc8-pb-3x hc8-clr-placeholder">
                        <span>{userName}</span>
                        <span className="cap-audit-event__dot">•</span>
                        <span>{dateText}</span>
                      </div>

                      <div className="cap-audit-event__cards">
                        {blocks.map((b) => (
                          <ApprovalTypeCard
                            key={`${idx}-${b.approvalTypeName}`}
                            title={b.approvalTypeName}
                            approved={b.approved}
                            removed={b.removed}
                            unchanged={b.unchanged}
                          />
                        ))}
                      </div>
                    </div>
                  </Timeline.Item>
                )
              })}
            </Timeline>
          ))}
      </Grid.Container>

      <Grid.Item
        size={{ xs: 12 }}
        className="cap-audit__footer-note hc8-mh-4x hc8-mt-3x hc8-mb-4x hc8-clr-disabled hc8-fs-100"
      >
        All changes are automatically tracked and timestamped
      </Grid.Item>
    </Grid.Container>
  )
}

export default CapabilityAuditTrail
